import {createSlice} from '@reduxjs/toolkit';
import {usersApi} from './ausersApi';
import type {RootState} from '../store';
import {Platform} from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { changeLanguage } from '../../i18n';

export interface AuthState {
  user: string;
  username: string;
  token: string;
  company: string;
  companyname: string;
  lang: string;
  addr1: string;
  addr2: string;
  city: string;
  state: string;
  pincode: string;
  phone: string;
  email: string;
  web: string;
  logo: string;
  today: string;
  tax1no: string;
  tax2no: string;
  tax3no: string;
  defsource: number;
  defplan: number;
  defmode: number;
  defidtype: number;
  defpurpose: number;
  // Check-in/out policy times (HH:mm); blank = not set, keep legacy 'now' behaviour
  checkintime?: string;
  checkouttime?: string;
  otacheckintime?: string;
  otacheckouttime?: string;
  default24h?: number;
  defstate: number;
  defcountry: number;
  vacant: string;
  occupied: string;
  group: string;
  houseguest: string;
  expcheckout: string;
  dirty: string;
  block: string;
  maintenance: string;
  menu?: (bt_amenu & bt_ausermenu)[];
  invoiceTemplate: number;
  invoiceCopies: number;
  grc: number;
  // Rental-mode company toggles - plan section 5/6.5. Mobile had no rental-type
  // infrastructure at all before this feature; mirrors ui/src/redux/services/
  // authSlice.ts's rentaldaily/rentalhourly/rentalrefreshment.
  rentaldaily?: number;
  rentalhourly?: number;
  rentalrefreshment?: number;
  mustChangePwd?: boolean;
}

const initialState: AuthState = {
  user: '',
  username: '',
  token: '',
  company: '',
  companyname: '',
  lang: 'en',
  addr1: '',
  addr2: '',
  city: '',
  state: '',
  pincode: '',
  phone: '',
  email: '',
  web: '',
  logo: '',
  today: '',
  tax1no: '',
  tax2no: '',
  tax3no: '',
  defsource: 0,
  defplan: 0,
  defmode: 0,
  defidtype: 0,
  defpurpose: 0,
  defstate: 0,
  defcountry: 0,
  vacant: '',
  occupied: '',
  group: '',
  houseguest: '',
  expcheckout: '',
  dirty: '',
  block: '',
  maintenance: '',
  invoiceTemplate: 0,
  invoiceCopies: 1,
  grc: 1,
  rentaldaily: 1,
  rentalhourly: 0,
  rentalrefreshment: 0,
};

export const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    updateState: (state, action) => {
      return {...state, ...action.payload};
    },
    resetState() {
      return initialState;
    },
    clearCache: (state) => {
      // Just an action for middleware to listen to
      return state;
    },
  },
  extraReducers: builder => {
    builder.addMatcher(
      usersApi.endpoints.signIn.matchFulfilled,
      (state, {payload}) => {
        state.user = payload.us_id;
        state.username = payload.us_name;
        state.token = payload.us_token;
        // Admin-flagged "change password at next login"; Home.tsx blocks the app until cleared
        state.mustChangePwd = ['1', 1, true].includes(payload.us_changepwd);
        state.company = payload.cm_id;
        state.companyname = payload.cm_name;
        state.lang = payload.lang || payload.us_language || payload.cm_language || 'en';
        changeLanguage(state.lang).catch((err) => console.error('Failed to sync language:', err));
        state.addr1 = payload.cm_addr1;
        state.addr2 = payload.cm_addr2;
        state.city = payload.cm_city;
        state.state = payload.cm_state;
        state.pincode = payload.cm_pincode;
        state.phone = payload.cm_phone;
        state.email = payload.cm_email;
        state.web = payload.cm_web;
        state.logo = payload.cm_logo || '';
        state.today = payload.se_date || new Date().toString();
        state.tax1no = payload.se_tax1no;
        state.tax2no = payload.se_tax2no;
        state.tax3no = payload.se_tax3no;
        state.defsource = payload.se_defsource;
        state.defplan = payload.se_defplan;
        state.defmode = payload.se_defmode;
        state.defidtype = payload.se_defidtype;
        state.defpurpose = payload.se_defpurpose;
        state.checkintime = payload.se_checkintime || '';
        state.checkouttime = payload.se_checkouttime || '';
        state.otacheckintime = payload.se_otacheckintime || '';
        state.otacheckouttime = payload.se_otacheckouttime || '';
        state.default24h = Number(payload.se_default24h) || 0;
        state.defstate = payload.se_state;
        state.defcountry = payload.se_country;
        state.vacant = payload.se_vacant || '#88E2F7';
        state.occupied = payload.se_occupied || '#8BF788';
        state.group = payload.se_group || '#E8EAB2';
        state.houseguest = payload.se_houseguest || '#CAC9EF';
        state.expcheckout = payload.se_expco || '#E2B2EA';
        state.dirty = payload.se_dirty || '#EADAEA';
        state.block = payload.se_block || '#F9F369';
        state.maintenance = payload.se_maintenance || '#EAB2D2';
        state.menu = payload.us_menu;
        state.grc = payload.se_grc;
        state.invoiceTemplate = payload.se_billformat;
        state.invoiceCopies = payload.se_copies;
        state.rentaldaily = payload.se_rentaldaily ?? 1;
        state.rentalhourly = payload.se_rentalhourly ?? 0;
        state.rentalrefreshment = payload.se_rentalrefreshment ?? 0;

        // Clear multiTab storage on login
        try {
          if (Platform.OS === 'web') {
            localStorage.removeItem('multiTab.tabs');
            localStorage.removeItem('multiTab.current');
          } else {
            AsyncStorage.multiRemove(['multiTab.tabs', 'multiTab.current']);
          }
        } catch (error) {
          console.error('Error clearing multiTab storage on login', error);
        }
      },
    );
  },
});

export default authSlice.reducer;
export const {updateState, resetState, clearCache} = authSlice.actions;

export const selectCurrentUser = (state: RootState) => state.gdata;

